import Badge from '../../ui/Badge'
import type { BadgeTone } from '../../ui/Badge'
import { classify } from '../../lib/verdict'
import { leakTest } from '../../content/site'
import type { DnsechoRow, IpDetails } from '@shared/api'
import styles from './VerdictPanel.module.css'

interface VerdictPanelProps {
    rows: DnsechoRow[]
    self: IpDetails | undefined
}

const TONE: Record<string, BadgeTone> = {
    ok: 'ok', warn: 'warn', leak: 'crit', unknown: 'neutral',
}

/**
 * The answer to the question the visitor actually came with. It sits above every
 * chart because "two organisations saw your queries, one is your ISP" is the
 * finding; the charts are supporting evidence.
 */
export default function VerdictPanel({ rows, self }: VerdictPanelProps) {
    const verdict = classify(rows, self)
    const { level, organisations, countries, operators, ispOperator } = verdict

    const detail =
        level === 'ok' ? leakTest.verdict.ok(rows.length, operators[0] ?? '—')
        : level === 'warn' ? leakTest.verdict.warn(organisations, countries)
        : level === 'leak' ? leakTest.verdict.leak(
            self?.asnOrg ?? self?.isp ?? 'your connection',
            rows.filter((r) => (r.asnOrg ?? r.isp) === ispOperator).length,
            ispOperator ?? '—')
        : leakTest.verdict.unknown

    /* The card itself is tinted with the verdict's own colour rather than
       sitting inside a neutral Panel: this is the answer the visitor came for,
       and it is the one thing on the page that should be legible from across
       the room. It is also what people screenshot, so it has to stand alone. */
    return (
        <section className={`${styles.card} ${styles[level]}`} aria-labelledby="verdict-label">
            <span id="verdict-label">
                <Badge tone={TONE[level]}>{leakTest.verdict.labels[level]}</Badge>
            </span>

            <p className={styles.hero}>
                <span className={styles.figure}>{organisations > 0 ? organisations : '—'}</span>
                <span className={styles.unit}>
                    {organisations > 0
                        ? `organisation${organisations === 1 ? '' : 's'} saw your DNS queries`
                        : 'not enough resolvers observed'}
                </span>
            </p>

            <p className={styles.detail}>{detail}</p>
        </section>
    )
}
